import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetPrintersQuery,
  useCreatePrinterMutation,
  useUpdatePrinterMutation,
} from "../../store/api";

const PRINTER_TYPES = ["LAN", "WINDOWS", "USB"];
const ADD_PRINTER_TYPE = "WINDOWS";

const initialForm = {
  name: "",
  printerType: ADD_PRINTER_TYPE,
  ipAddress: "",
  port: "",
  driverName: "",
  isActive: true,
};

const getApiErrorCode = (err) =>
  err?.data?.code || err?.data?.errorCode || err?.data?.title || null;

const getPrinterErrorMessage = (err, fallback) => {
  const code = getApiErrorCode(err);
  const errors = {
    PRINTER_NAME_EXISTS: "Printer name already exists.",
    PRINTER_NAME_REQUIRED: "Printer name is required.",
    PRINTER_TYPE_REQUIRED: "Printer type is required.",
    PRINTER_TYPE_INVALID: "Printer type is invalid.",
    PRINTER_IP_REQUIRED: "IP address is required for LAN printers.",
    PRINTER_PORT_REQUIRED: "Port is required for LAN printers.",
    PRINTER_DRIVER_REQUIRED: "Driver name is required for Windows/USB printers.",
    PRINTER_NOT_FOUND: "Selected printer is no longer available.",
  };
  return (
    errors[code] ||
    err?.data?.detail ||
    err?.data?.message ||
    fallback ||
    "Something went wrong. Please try again."
  );
};

const isValidIp = (ip) => {
  if (!ip) return false;
  const ipPattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipPattern.test(ip)) return false;
  return ip.split(".").every((part) => {
    const num = Number(part);
    return Number.isInteger(num) && num >= 0 && num <= 255;
  });
};

const validatePrinterForm = (form) => {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = "Printer name is required.";
  }
  if (!form.printerType) {
    errors.printerType = "Printer type is required.";
  }

  if (form.printerType === "LAN") {
    if (!form.ipAddress.trim()) {
      errors.ipAddress = "IP address is required.";
    } else if (!isValidIp(form.ipAddress.trim())) {
      errors.ipAddress = "Enter a valid IPv4 address.";
    }
    if (!form.port.trim()) {
      errors.port = "Port is required.";
    } else if (!Number.isInteger(Number(form.port))) {
      errors.port = "Port must be a whole number.";
    }
  } else if (form.printerType === "WINDOWS" || form.printerType === "USB") {
    if (!form.driverName.trim()) {
      errors.driverName = "Driver name is required.";
    }
  }

  return errors;
};

const PrinterForm = () => {
  const navigate = useNavigate();
  const { printerId } = useParams();
  const isEditing = Boolean(printerId);

  const { data, isLoading, isError, error } = useGetPrintersQuery();
  const [createPrinter, createState] = useCreatePrinterMutation();
  const [updatePrinter, updateState] = useUpdatePrinterMutation();

  const printers = Array.isArray(data) ? data : [];
  const currentPrinter = useMemo(
    () => printers.find((p) => p.printerId === printerId) || null,
    [printers, printerId],
  );

  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setForm(initialForm);
      setFormErrors({});
      return;
    }
    if (currentPrinter) {
      setForm({
        name: currentPrinter.name || "",
        printerType: currentPrinter.printerType || "",
        ipAddress: currentPrinter.ipAddress || "",
        port:
          currentPrinter.port !== null && currentPrinter.port !== undefined
            ? String(currentPrinter.port)
            : "",
        driverName: currentPrinter.driverName || "",
        isActive:
          currentPrinter.isActive !== undefined ? currentPrinter.isActive : true,
      });
      setFormErrors({});
    }
  }, [currentPrinter, isEditing]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormErrors({});
    setForm((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      const next = {
        ...prev,
        [name]: nextValue,
      };

      // Add Printer: keep driver in sync with printer name and lock type to WINDOWS.
      if (!isEditing) {
        next.printerType = ADD_PRINTER_TYPE;
        if (name === "name") {
          next.driverName = value;
        }
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    const normalizedForm = !isEditing
      ? {
          ...form,
          printerType: ADD_PRINTER_TYPE,
          driverName: form.name,
        }
      : form;

    const errors = validatePrinterForm(normalizedForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      name: normalizedForm.name.trim(),
      printerType: normalizedForm.printerType,
      ipAddress:
        normalizedForm.printerType === "LAN"
          ? normalizedForm.ipAddress.trim()
          : null,
      port:
        normalizedForm.printerType === "LAN"
          ? Number(normalizedForm.port) || null
          : null,
      driverName:
        normalizedForm.printerType === "LAN"
          ? null
          : normalizedForm.driverName.trim() || null,
      isActive: normalizedForm.isActive,
    };

    try {
      if (isEditing) {
        await updatePrinter({ printerId, payload }).unwrap();
      } else {
        await createPrinter(payload).unwrap();
        setForm(initialForm);
        setShowSuccessAlert(true);
        setTimeout(() => {
          setShowSuccessAlert(false);
          navigate("/settings/printers");
        }, 1200);
      }
    } catch (err) {
      setFormErrors({
        form: getPrinterErrorMessage(err, "Failed to save printer."),
      });
    }
  };

  const handleBack = () => navigate("/settings/printers");

  if (isEditing && isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit Printer" subtitle="Loading printer..." />
        <Card>
          <Alert tone="info">Loading printer details...</Alert>
        </Card>
      </div>
    );
  }

  if (isEditing && isError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit Printer" subtitle="Unable to load printer." />
        <Card>
          <Alert tone="error">
            {getPrinterErrorMessage(error, "Failed to load printer.")}
          </Alert>
        </Card>
      </div>
    );
  }

  if (isEditing && !isLoading && !currentPrinter) {
    return (
      <div className="space-y-6">
        <PageHeader title="Edit Printer" subtitle="Printer not found." />
        <Card>
          <Alert tone="warning">
            The selected printer could not be found. It may have been deleted.
          </Alert>
          <div className="mt-4">
            <Button size="sm" onClick={handleBack}>
              Back to Printers
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isEditing ? "Edit Printer" : "Add Printer"}
        subtitle="Configure printer connection details."
        actions={
          <Button size="sm" variant="secondary" onClick={handleBack}>
            Back
          </Button>
        }
      />

      <Card>
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {isEditing ? "Printer Details" : "New Printer"}
        </h2>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Printer Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              error={formErrors.name}
              required
            />
            {isEditing ? (
              <Select
                label="Printer Type"
                name="printerType"
                value={form.printerType}
                onChange={handleChange}
                error={formErrors.printerType}
                required
              >
                {PRINTER_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                label="Printer Type"
                name="printerType"
                value={ADD_PRINTER_TYPE}
                readOnly
                className="cursor-not-allowed bg-slate-100 dark:bg-slate-800"
              />
            )}
          </div>

          {form.printerType === "LAN" && (
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="IP Address"
                name="ipAddress"
                value={form.ipAddress}
                onChange={handleChange}
                error={formErrors.ipAddress}
                placeholder="192.168.1.231"
                required
              />
              <Input
                label="Port"
                name="port"
                value={form.port}
                onChange={handleChange}
                error={formErrors.port}
                placeholder="9100"
                required
              />
            </div>
          )}

          {(form.printerType === "WINDOWS" ||
            form.printerType === "USB") && (
            <Input
              label="Driver Name"
              name="driverName"
              value={form.driverName}
              onChange={handleChange}
              error={formErrors.driverName}
              readOnly={!isEditing}
              className={
                !isEditing
                  ? "cursor-not-allowed bg-slate-100 dark:bg-slate-800"
                  : ""
              }
              helper={
                !isEditing ? "Auto-filled from Printer Name." : undefined
              }
              placeholder="EPSON TM-T82X-II Receipt"
              required
            />
          )}

          <Checkbox
            label="Active"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
            helper="Inactive printers are hidden from station mapping."
          />

          {formErrors.form && <Alert tone="error">{formErrors.form}</Alert>}
          {(createState.isError || updateState.isError) && (
            <Alert tone="error">
              {getPrinterErrorMessage(
                createState.error || updateState.error,
                "Failed to save printer.",
              )}
            </Alert>
          )}
          {showSuccessAlert && !isEditing && (
            <Alert tone="success">Printer added successfully.</Alert>
          )}
          {isEditing &&
            (createState.isSuccess || updateState.isSuccess) && (
              <Alert tone="success">Printer updated successfully.</Alert>
            )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {isEditing ? "Update Printer" : "Add Printer"}
            </Button>
            <Button type="button" variant="secondary" onClick={handleBack}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>

    </div>
  );
};

export default PrinterForm;
